import { DatePipe } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BaseDataService, HttpResponsesHandlersFactory } from "@bbwt/modules/data-service";
import { GlobalDroSearchRecord } from "@project/models/global-dro/globaldro-searchrecord";
import { GdroDrugData } from "@project/models/global-dro/globaldro-drugdata";
import { GdroDrugStatus } from "@project/models/global-dro/globaldro-drugstatus";
import { GlobalDroSearchResult } from "@project/models/global-dro/search-result";
import { map } from "rxjs";
import { BroadcastService } from "@bbwt/modules/broadcasting";

@Injectable({
    providedIn: "root"
})
export class GlobalDroService extends BaseDataService {
    public readonly url = "api/globaldro";
    public readonly entityTitle = "GlobalDRO";

    static readonly RequiredGlobalDroTermsEventName = "RequiredGlobalDroTerms";
    static readonly GlobalDroTermsAcceptedEventName = "GlobalDroTermsAccepted";

    constructor(http: HttpClient,
                handlersFactory: HttpResponsesHandlersFactory,
                private broadcastService: BroadcastService) {
        super(http, handlersFactory);
    }

    triggerRequiredGlobalDroTermsAcceptance(attemptedUrl?: string): void {
        this.broadcastService.broadcast(
            GlobalDroService.RequiredGlobalDroTermsEventName,
            attemptedUrl || "/"
        );
    }

    broadcastGlobalDroTermsAccepted(): void {
        this.broadcastService.broadcast(GlobalDroService.GlobalDroTermsAcceptedEventName);
    }

    globalDroSearch(searchTerm: string) {
        return this.handleRequest(
            this.http.get<GlobalDroSearchResult>(`${this.url}/search`, { params: { searchTerm } }),
            this.handlersFactory.getDefault()
        );
    }

    ingredientSearch(ingredient: string) {
        return this.handleRequest(
            this.http
                .get<string>(`${this.url}/ingredient`, { params: { ingredient } })
                .pipe(map(ingredient => this.parseDrugData(ingredient))),
            this.handlersFactory.getDefault()
        );
    }

    brandSearch(brand: string) {
        return this.handleRequest(
            this.http.get<string>(`${this.url}/brand`, { params: { brand } }).pipe(map(brand => this.parseDrugData(brand))),
            this.handlersFactory.getDefault()
        );
    }

    storeSearchData(GlobalDroSearchRecord: GlobalDroSearchRecord) {
        return this.handleRequest(
            this.http.post<void>(`${this.url}/${GlobalDroSearchRecord.precriptionlineId}/store`, GlobalDroSearchRecord),
            this.handlersFactory.getDefault()
        );
    }

    mapSearchRecord(gdroDrugData: GdroDrugData, gdroDrugStatus: GdroDrugStatus, prescriptionLineId: number = null): GlobalDroSearchRecord {
        const status = this.createStatus(gdroDrugStatus);

        const localSearchRecord: GlobalDroSearchRecord = {
            id: null,
            drugId: gdroDrugData?.id ?? "",
            drugName: gdroDrugData?.name ?? "",
            language: gdroDrugData?.details.language ?? "",
            nationOfPurchase: gdroDrugData?.details.nationOfPurchase ?? "",
            referenceNumber: gdroDrugData?.details.referenceNumber ?? "",
            sport: gdroDrugData?.details.sport ?? "",

            inCompStatus: gdroDrugStatus?.inCompStatus.statusKey ?? "",
            outCompStatus: gdroDrugStatus?.outOfCompStatus.statusKey ?? "",
            route: gdroDrugStatus?.routeOfAdministration.name ?? "",

            searchDateTimeStamp: this.createDateTimeStamp(gdroDrugData?.details.dateTimeStamp ?? ""),

            precriptionlineId: prescriptionLineId ?? null,
            status
        };

        return localSearchRecord;
    }

    findMatchingStatus(statuses: GdroDrugStatus[], thisroute: string): GdroDrugStatus {
        const matchingStatus: GdroDrugStatus = statuses.find(
            status => status.routeOfAdministration.name?.toLowerCase().trim() === thisroute?.toLowerCase().trim()
        );

        if (matchingStatus) {
            return matchingStatus;
        } else {
            return null;
        }
    }

    getRouteIndependantStatus(statuses: GdroDrugStatus[]): GdroDrugStatus {
        let matchingStatus: GdroDrugStatus = this.findMatchingStatus(statuses, "Route Independent "); //Trailing white space present in GlobalDro API
        return matchingStatus;
    }

    createDateTimeStamp(dateString: string): Date {
        if (!dateString) {
            return new Date(0);
        }
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return new Date(0);
        }
        const datePipe = new DatePipe("en-US");
        const formattedDate = datePipe.transform(date, "yyyy-MM-ddTHH:mm:ss");
        return formattedDate ? new Date(formattedDate) : new Date();
    }

    mapRoutes(passedroute: string): string {
        if (!passedroute) {
            return "mis-match";
        }

        const normalizedRoute = passedroute.toLowerCase();

        const routeMapping: { [key: string]: string } = {
            // Otic routes
            "auricular": "Otic",
            "otic": "Otic",

            // Topical - dermatological routes
            "topical": "Topical - dermatological",
            "topically": "Topical - dermatological",
            "topical (eye)": "Topical - dermatological",
            "top": "Topical - dermatological",
            "transdermal": "Topical - dermatological",
            "scalp": "Topical - dermatological",
            "iontophoresis": "Topical - dermatological",
            "nail": "Topical - dermatological",
            "bathadditive": "Topical - dermatological",

            // Ophthalmic routes
            "ophthalmic": "Ophthalmic",
            "ocular": "Ophthalmic",
            "intraocular": "Ophthalmic",

            // Nasal routes
            "intranasal": "Nasal",
            "endosinusial": "Nasal",
            "nasal": "Nasal",

            // Inhalation - by device
            "inhaled": "Inhalation - by device",
            "inhalation": "Inhalation - by device",

            // Inhalation - nebulization
            "nebulizer": "Inhalation - nebulization",
            "neb": "Inhalation - nebulization",

            // Rectal routes
            "rectal": "Rectal",
            "pr": "Rectal",
            "submucosalrectal": "Rectal",
            "bodycavity": "Rectal",

            // Oral routes
            "oral": "Oral",
            "sl": "Oral",
            "liquid": "Oral",
            "oromucosal": "Oral",
            "sublingual": "Oral",
            "submucosal": "Oral",
            "dental": "Oral",
            "buccal": "Oral",
            "endotracheopulmonary": "Oral",
            "gastroenteral": "Oral",
            "gingival": "Oral",

            // Injection - intra-articular
            "intra-articular": "Injection - intra-articular",

            // Injection - local
            "epidural": "Injection - local",
            "intraarticular": "Injection - local",
            "intradiscal": "Injection - local",
            "ia": "Injection - local",

            // Injection - intravenous
            "intravenous": "Injection - intravenous",
            "iv": "Injection - intravenous",
            "intraarterial": "Injection - intravenous",
            "intraosseous": "Injection - intravenous",
            "intracardiac": "Injection - intravenous",
            "intracavernous": "Injection - intravenous",
            "intracerebroventricular": "Injection - intravenous",
            "intracoronary": "Injection - intravenous",
            "intraputaminal": "Injection - intravenous",
            "haemodialysis": "Injection - intravenous",

            // Injection - intra-muscular
            "im": "Injection - intra-muscular",
            "intra-muscular": "Injection - intra-muscular",
            "intramuscular": "Injection - intra-muscular",
            "intracameral": "Injection - intra-muscular",
            "intraglandular": "Injection - intra-muscular",
            "intramuscular-deep": "Injection - intra-muscular",
            "intraperitoneal": "Injection - intra-muscular",
            "intrauterine": "Injection - intra-muscular",
            "intrapleural": "Injection - intra-muscular",
            "intrathecal": "Injection - intra-muscular",
            "intraventricular": "Injection - intra-muscular",
            "intraventricular cardiac": "Injection - intra-muscular",
            "intravesical": "Injection - intra-muscular",
            "intracholangiopancreatic": "Injection - intra-muscular",
            "perineural": "Injection - intra-muscular",
            "peritumoral": "Injection - intra-muscular",
            "subretinal": "Injection - intra-muscular",
            "intrabursal": "Injection - intra-muscular",

            // Injection - subcutaneous
            "subcutaneous": "Injection - subcutaneous",
            "cutaneous": "Injection - subcutaneous",
            "peribulbar ocular": "Injection - subcutaneous",
            "periarticular": "Injection - subcutaneous",
            "insert": "Injection - subcutaneous",
            "intradermal": "Injection - subcutaneous",
            "intraepidermal": "Injection - subcutaneous",
            "intralesional": "Injection - subcutaneous",
            "intravitreal": "Injection - subcutaneous",
            "infiltration": "Injection - subcutaneous",

            // Vaginal routes
            "vaginal": "Vaginal",
            "pv": "Vaginal",
            "endocervical": "Vaginal",
            "urethral": "Vaginal",

            // Not applicable
            "route of administration not applicable": "N/A",

            // Explicitly mismatched routes
            "blue": "mis-match",
            "epilesional": "mis-match",
            "extraamniotic": "mis-match",
            "extracorporeal": "mis-match",
            "haemofiltration": "mis-match",
            "linelock": "mis-match",
            "regionalperfusion": "mis-match",
            "n/a": "mis-match"
        };

        return routeMapping[normalizedRoute] ?? passedroute;
    }

    private parseDrugData(data: string) {
        try {
            return <GdroDrugData>JSON.parse(data);
        } catch {
            return null;
        }
    }

    private createStatus(gdroDrugStatus: GdroDrugStatus) {
        if (gdroDrugStatus?.inCompStatus?.statusKey === "CONDITIONAL" || gdroDrugStatus?.outOfCompStatus?.statusKey === "CONDITIONAL") {
            return "conditional";
        }

        if (!gdroDrugStatus?.routeOfAdministration?.key) {
            return "info";
        }

        if (gdroDrugStatus?.inCompStatus?.statusKey === "PROHIBITED" || gdroDrugStatus?.outOfCompStatus?.statusKey === "PROHIBITED") {
            return "warn";
        }

        return "success";
    }
}
